import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, ArrowLeft, RefreshCw, ClipboardList, Phone, Mail, MapPin,
  CheckCircle2, XCircle, Truck, PackageCheck, Clock, Tag, AlertCircle, Copy
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { adminAxios } from '../lib/adminApi';

const STATUS_META = {
  pending:    { label: 'Pending',    color: 'text-yellow-400',  bg: 'bg-yellow-500/10 border-yellow-500/30',  icon: Clock },
  accepted:   { label: 'Accepted',   color: 'text-blue-400',    bg: 'bg-blue-500/10 border-blue-500/30',      icon: CheckCircle2 },
  processing: { label: 'Processing', color: 'text-purple-400',  bg: 'bg-purple-500/10 border-purple-500/30',  icon: RefreshCw },
  shipped:    { label: 'Shipped',    color: 'text-cyan-400',    bg: 'bg-cyan-500/10 border-cyan-500/30',      icon: Truck },
  delivered:  { label: 'Delivered',  color: 'text-green-400',   bg: 'bg-green-500/10 border-green-500/30',    icon: PackageCheck },
  cancelled:  { label: 'Cancelled',  color: 'text-red-400',     bg: 'bg-red-500/10 border-red-500/30',        icon: XCircle },
  refunded:   { label: 'Refunded',   color: 'text-orange-400',  bg: 'bg-orange-500/10 border-orange-500/30',  icon: RefreshCw },
};

const TABS = ['all', 'pending', 'accepted', 'processing', 'shipped', 'delivered', 'cancelled', 'refunded'];

const formatINR = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

export const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [dispatch, setDispatch] = useState(null);
  const [showDispatch, setShowDispatch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const [serviceTypes, setServiceTypes] = useState({ service_types: [], default: null, configured: false });

  useEffect(() => {
    adminAxios.get('/admin/eshipz/service-types')
      .then(r => setServiceTypes(r.data))
      .catch(() => { /* silent */ });
  }, []);

  const fetchAll = async () => {
    try {
      const [ordersRes, statsRes] = await Promise.all([
        adminAxios.get(`/admin/orders${activeTab !== 'all' ? `?status=${activeTab}` : ''}`),
        adminAxios.get('/admin/orders/stats'),
      ]);
      setOrders(ordersRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [activeTab]);

  const fetchDispatch = async () => {
    try {
      const { data } = await adminAxios.get('/admin/orders/dispatch/pending');
      setDispatch(data);
      setShowDispatch(true);
    } catch {
      toast.error('Failed to load dispatch sheet');
    }
  };

  const updateStatus = async (orderId, status, extras = {}) => {
    setActionLoading(`${orderId}-${status}`);
    try {
      const { data } = await adminAxios.put(`/admin/orders/${orderId}/status`, { status, ...extras });
      if (data.refund) {
        toast.success(`Order cancelled — refund ${data.refund.status} for ${formatINR(data.refund.amount)}`);
      } else {
        toast.success(`Order → ${STATUS_META[status]?.label || status}`);
      }
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Status update failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleShip = async (orderId, order) => {
    const method = (order?.payment_method || '').toLowerCase();
    // In-person pickup / manual courier orders — no eShipz call, just record AWB
    if (method === 'pickup') {
      const awb = window.prompt('Courier name & tracking / AWB number (e.g. "India Post RX123456789"):');
      if (!awb) return;
      const url = window.prompt('Tracking URL (optional):', '');
      await updateStatus(orderId, 'shipped', { tracking_awb: awb, tracking_url: url || null });
      return;
    }

    // Try auto-shipment via eShipz for Razorpay-paid or COD orders
    if (serviceTypes.configured && serviceTypes.service_types?.length) {
      const defaultSvc = method === 'cod'
        ? (serviceTypes.service_types.find(s => s.includes('COD')) || serviceTypes.default)
        : serviceTypes.default || 'eTailPrePaidAir';
      const codHint = method === 'cod' ? `\n\n⚠️ COD order — ₹${order?.total_amount} will be collected by courier.` : '';
      const svc = window.prompt(
        `Ship with BlueDart via eShipz?${codHint}\n\nEnter service type, or leave blank to cancel and use manual AWB.\n\nAvailable: ${serviceTypes.service_types.join(', ')}`,
        defaultSvc
      );
      if (svc && svc.trim()) {
        setActionLoading(`${orderId}-shipped`);
        try {
          const { data } = await adminAxios.post(`/admin/orders/${orderId}/create-shipment`, { service_type: svc.trim() });
          const awb = data?.eshipz?.awb;
          toast.success(`Shipped! AWB: ${awb} · Rate ₹${data?.eshipz?.rate ?? '—'}`, { duration: 6000 });
          await fetchAll();
          return;
        } catch (err) {
          const detail = err?.response?.data?.detail || 'eShipz call failed';
          toast.error(detail, { duration: 8000 });
          // Fall through to manual entry
        } finally {
          setActionLoading(null);
        }
      }
    }
    // Manual AWB entry fallback
    const awb = window.prompt('Enter AWB / tracking number from courier:');
    if (!awb) return;
    const url = window.prompt('Tracking URL (optional):', '');
    await updateStatus(orderId, 'shipped', { tracking_awb: awb, tracking_url: url || null });
  };

  const handleCancel = async (orderId, paid) => {
    const msg = paid
      ? 'Cancel this PAID order and issue refund via Razorpay?'
      : 'Cancel this order?';
    if (!window.confirm(msg)) return;
    const reason = window.prompt('Cancellation reason:', paid ? 'Customer requested refund' : 'Out of stock');
    if (reason === null) return;
    await updateStatus(orderId, 'cancelled', { cancellation_reason: reason });
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const StatusBadge = ({ status }) => {
    const meta = STATUS_META[status] || STATUS_META.pending;
    const Icon = meta.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-widest px-3 py-1 border rounded-full ${meta.bg} ${meta.color}`}>
        <Icon size={12} strokeWidth={1.8} /> {meta.label}
      </span>
    );
  };

  return (
    <div data-testid="admin-orders-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <Link to="/admin" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-3 transition-colors">
              <ArrowLeft size={16} className="mr-2" strokeWidth={1.5} /> Back to Admin
            </Link>
            <h1 className="font-display text-5xl md:text-6xl font-normal tracking-tight text-foreground flex items-center gap-4">
              <Package className="text-primary" size={48} strokeWidth={1.5} />
              <span>Order <span className="italic">Management</span></span>
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchDispatch}
              data-testid="dispatch-sheet-button"
              className="border border-primary/30 text-primary hover:bg-primary/10 rounded-full px-6 py-3 uppercase tracking-widest text-xs font-medium flex items-center gap-2 transition-all"
            >
              <ClipboardList size={16} strokeWidth={1.5} />
              <span>Dispatch Sheet</span>
            </button>
            <button
              onClick={fetchAll}
              className="border border-white/20 hover:border-primary hover:text-primary rounded-full px-6 py-3 uppercase tracking-widest text-xs font-medium flex items-center gap-2 transition-all"
            >
              <RefreshCw size={16} strokeWidth={1.5} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 mb-8">
          {TABS.map(t => {
            const meta = STATUS_META[t];
            const count = t === 'all' ? (stats.total || 0) : (stats[t] || 0);
            const active = activeTab === t;
            return (
              <button
                key={t}
                onClick={() => setActiveTab(t)}
                data-testid={`tab-${t}`}
                className={`p-3 border text-left transition-all ${active ? 'border-primary bg-primary/5' : 'border-white/10 hover:border-primary/40'}`}
              >
                <p className={`text-xs uppercase tracking-widest ${active ? 'text-primary' : 'text-muted-foreground'}`}>
                  {t === 'all' ? 'All' : meta?.label || t}
                </p>
                <p className="font-display text-2xl mt-1">{count}</p>
              </button>
            );
          })}
        </div>

        {/* Orders list */}
        {loading ? (
          <p className="text-muted-foreground">Loading orders...</p>
        ) : orders.length === 0 ? (
          <div className="border border-white/10 p-12 text-center">
            <AlertCircle size={36} className="mx-auto text-muted-foreground mb-3" strokeWidth={1.2} />
            <p className="text-muted-foreground italic">No orders in this view.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map(o => {
              const expanded = expandedId === o.id;
              const paid = o.payment_status === 'completed';
              const status = o.order_status || 'pending';
              const canAccept = status === 'pending';
              const canProcess = status === 'accepted';
              const canShip = status === 'processing';
              const canDeliver = status === 'shipped';
              const canCancel = ['pending', 'accepted', 'processing'].includes(status);

              return (
                <div key={o.id} className="border border-white/10 hover:border-primary/30 transition-colors">
                  <div
                    onClick={() => setExpandedId(expanded ? null : o.id)}
                    className="p-5 cursor-pointer flex flex-col md:flex-row md:items-center gap-4"
                    data-testid={`order-row-${o.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap mb-2">
                        <StatusBadge status={status} />
                        {paid && <span className="text-xs text-green-400 border border-green-500/30 px-2 py-0.5 rounded-full">PAID</span>}
                        {(o.payment_method === 'cod' || o.payment_status === 'cod_pending') && (
                          <span className="text-xs text-orange-400 border border-orange-500/30 px-2 py-0.5 rounded-full" data-testid={`badge-cod-${o.id}`}>COD ₹{Number(o.total_amount || 0).toFixed(0)}</span>
                        )}
                        {o.payment_method === 'pickup' && (
                          <span className="text-xs text-purple-400 border border-purple-500/30 px-2 py-0.5 rounded-full" data-testid={`badge-pickup-${o.id}`}>PICKUP / MANUAL</span>
                        )}
                        {o.coupon_code && (
                          <span className="text-xs text-primary border border-primary/30 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Tag size={10} strokeWidth={1.8} /> {o.coupon_code}
                          </span>
                        )}
                        <code className="text-xs text-muted-foreground">#{o.id.slice(0, 8)}</code>
                      </div>
                      <p className="font-medium text-lg">{o.customer_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {o.items?.length || 0} item{(o.items?.length || 0) !== 1 ? 's' : ''} · {formatDate(o.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-accent text-2xl italic text-primary">{formatINR(o.total_amount)}</p>
                      {o.shipping_method && <p className="text-xs text-muted-foreground mt-1">{o.shipping_method}</p>}
                    </div>
                  </div>

                  <AnimatePresence initial={false}>
                    {expanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="border-t border-white/10 overflow-hidden"
                      >
                        <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6 bg-background/40">
                          {/* Customer + Shipping */}
                          <div className="space-y-4">
                            <div>
                              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Customer</p>
                              <p className="font-medium">{o.customer_name}</p>
                              <p className="text-sm flex items-center gap-2 mt-1">
                                <Mail size={14} className="text-muted-foreground" strokeWidth={1.5} />
                                <span>{o.customer_email}</span>
                                <button onClick={(e) => { e.stopPropagation(); copy(o.customer_email, 'Email'); }} className="text-muted-foreground hover:text-primary">
                                  <Copy size={12} strokeWidth={1.5} />
                                </button>
                              </p>
                              <p className="text-sm flex items-center gap-2 mt-1">
                                <Phone size={14} className="text-muted-foreground" strokeWidth={1.5} />
                                <a href={`tel:${o.customer_phone}`} className="hover:text-primary">{o.customer_phone}</a>
                                <button onClick={(e) => { e.stopPropagation(); copy(o.customer_phone, 'Phone'); }} className="text-muted-foreground hover:text-primary">
                                  <Copy size={12} strokeWidth={1.5} />
                                </button>
                              </p>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Shipping Address</p>
                              <p className="text-sm flex items-start gap-2">
                                <MapPin size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                                <span>
                                  {o.shipping_address}
                                  {o.shipping_city && <>, {o.shipping_city}</>}
                                  {o.shipping_state && <>, {o.shipping_state}</>}
                                  {o.shipping_pincode && <> — {o.shipping_pincode}</>}
                                </span>
                              </p>
                              {o.tracking_awb && (
                                <p className="text-sm mt-2 flex items-center gap-2 flex-wrap">
                                  <Truck size={14} className="text-cyan-400" strokeWidth={1.5} />
                                  <span className="text-muted-foreground">AWB:</span>
                                  <code className="text-cyan-400">{o.tracking_awb}</code>
                                  {o.tracking_url && (
                                    <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-primary hover:underline text-xs">
                                      Track →
                                    </a>
                                  )}
                                  {o.eshipz_label_url && (
                                    <a
                                      href={o.eshipz_label_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      data-testid={`download-label-${o.id}`}
                                      className="text-green-400 hover:underline text-xs ml-2"
                                    >
                                      📄 Download Label PDF
                                    </a>
                                  )}
                                  {o.eshipz_service_type && (
                                    <span className="text-xs text-muted-foreground ml-2">({o.eshipz_service_type})</span>
                                  )}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Items */}
                          <div className="lg:col-span-1">
                            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Items to Dispatch</p>
                            <div className="space-y-2">
                              {(o.items || []).map((item, idx) => (
                                <div key={idx} className="flex justify-between items-center border-b border-white/5 pb-2">
                                  <div>
                                    <p className="text-sm font-medium">{item.name || item.product_id}</p>
                                    <p className="text-xs text-muted-foreground">Qty: <span className="text-primary font-medium">{item.quantity}</span> × {formatINR(item.price)}</p>
                                  </div>
                                  <p className="text-sm">{formatINR((item.price || 0) * (item.quantity || 0))}</p>
                                </div>
                              ))}
                            </div>
                            <div className="mt-3 pt-3 border-t border-white/10 space-y-1 text-sm">
                              {o.discount_amount > 0 && (
                                <div className="flex justify-between text-green-400">
                                  <span>Discount{o.coupon_code ? ` (${o.coupon_code})` : ''}</span>
                                  <span>− {formatINR(o.discount_amount)}</span>
                                </div>
                              )}
                              {o.shipping_cost > 0 && (
                                <div className="flex justify-between text-muted-foreground">
                                  <span>Shipping</span>
                                  <span>{formatINR(o.shipping_cost)}</span>
                                </div>
                              )}
                              <div className="flex justify-between font-medium pt-1 border-t border-white/10">
                                <span>Total</span>
                                <span className="text-primary">{formatINR(o.total_amount)}</span>
                              </div>
                            </div>
                          </div>

                          {/* Actions + history */}
                          <div className="space-y-3">
                            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Actions</p>
                            <div className="flex flex-wrap gap-2">
                              {canAccept && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); updateStatus(o.id, 'accepted'); }}
                                  disabled={actionLoading === `${o.id}-accepted`}
                                  data-testid={`accept-order-${o.id}`}
                                  className="bg-blue-500/20 border border-blue-500/40 text-blue-300 hover:bg-blue-500/30 px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-50 flex items-center gap-2"
                                >
                                  <CheckCircle2 size={14} strokeWidth={1.8} /> Accept
                                </button>
                              )}
                              {canProcess && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); updateStatus(o.id, 'processing'); }}
                                  data-testid={`process-order-${o.id}`}
                                  className="bg-purple-500/20 border border-purple-500/40 text-purple-300 hover:bg-purple-500/30 px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2"
                                >
                                  <RefreshCw size={14} strokeWidth={1.8} /> Mark Processing
                                </button>
                              )}
                              {canShip && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleShip(o.id, o); }}
                                  data-testid={`ship-order-${o.id}`}
                                  className="bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/30 px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2"
                                >
                                  <Truck size={14} strokeWidth={1.8} /> Mark Shipped
                                </button>
                              )}
                              {canDeliver && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); updateStatus(o.id, 'delivered'); }}
                                  data-testid={`deliver-order-${o.id}`}
                                  className="bg-green-500/20 border border-green-500/40 text-green-300 hover:bg-green-500/30 px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2"
                                >
                                  <PackageCheck size={14} strokeWidth={1.8} /> Confirm Delivery
                                </button>
                              )}
                              {canCancel && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleCancel(o.id, paid); }}
                                  data-testid={`cancel-order-${o.id}`}
                                  className="bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2"
                                >
                                  <XCircle size={14} strokeWidth={1.8} />
                                  {paid ? 'Cancel & Refund' : 'Cancel'}
                                </button>
                              )}
                              {!canAccept && !canProcess && !canShip && !canDeliver && !canCancel && (
                                <p className="text-xs text-muted-foreground italic">No actions available for this status.</p>
                              )}
                            </div>

                            {o.admin_notes && (
                              <div className="mt-3 p-3 border border-white/10 bg-white/5 rounded text-xs">
                                <p className="uppercase tracking-widest text-muted-foreground mb-1">Notes</p>
                                <p className="text-foreground">{o.admin_notes}</p>
                              </div>
                            )}

                            {o.cancellation_reason && (
                              <div className="mt-3 p-3 border border-red-500/20 bg-red-500/5 rounded text-xs">
                                <p className="uppercase tracking-widest text-red-400 mb-1">Cancellation Reason</p>
                                <p>{o.cancellation_reason}</p>
                              </div>
                            )}

                            {o.status_history && o.status_history.length > 0 && (
                              <div className="mt-3">
                                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Timeline</p>
                                <div className="space-y-1 text-xs">
                                  {o.status_history.map((h, idx) => (
                                    <div key={idx} className="flex gap-2 text-muted-foreground">
                                      <span className="text-primary">{h.to}</span>
                                      <span>{formatDate(h.at)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}

        {/* Dispatch modal */}
        <AnimatePresence>
          {showDispatch && dispatch && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6"
              onClick={() => setShowDispatch(false)}
            >
              <motion.div
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 40, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-background border border-primary/30 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
                data-testid="dispatch-modal"
              >
                <div className="sticky top-0 bg-background p-6 border-b border-white/10 flex justify-between items-center">
                  <div>
                    <h3 className="font-display text-3xl">Dispatch <span className="italic">Sheet</span></h3>
                    <p className="text-xs text-muted-foreground uppercase tracking-widest mt-1">
                      Items to prepare — from accepted + processing orders
                    </p>
                  </div>
                  <button onClick={() => setShowDispatch(false)} className="text-muted-foreground hover:text-foreground">
                    <XCircle size={24} strokeWidth={1.5} />
                  </button>
                </div>
                <div className="p-6 space-y-6">
                  {dispatch.dispatch_list.length === 0 ? (
                    <p className="text-muted-foreground italic text-center py-8">Nothing to dispatch right now — accept or process orders first.</p>
                  ) : (
                    <>
                      <div>
                        <h4 className="font-display text-xl mb-3">Total Quantities</h4>
                        <div className="space-y-2">
                          {dispatch.dispatch_list.map((d, idx) => (
                            <div key={idx} className="flex justify-between border-b border-white/10 pb-2">
                              <span>{d.name}</span>
                              <span className="font-accent text-xl italic text-primary">× {d.total_quantity}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-display text-xl mb-3">Per-Order Breakdown ({dispatch.orders.length})</h4>
                        <div className="space-y-3">
                          {dispatch.orders.map((o) => (
                            <div key={o.order_id} className="border border-white/10 p-3">
                              <div className="flex justify-between items-center mb-2">
                                <span className="font-medium">{o.customer}</span>
                                <StatusBadge status={o.status} />
                              </div>
                              <ul className="text-xs text-muted-foreground space-y-0.5">
                                {o.items.map((i, idx) => (
                                  <li key={idx}>• {i.name} × {i.quantity}</li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
